import React, { useState, useMemo, useRef, useEffect, useCallback } from 'react';
import { ArrowLeft, Search, Store, Waves, ChevronRight, ShoppingBag } from 'lucide-react';
import { motion } from 'framer-motion';
import { Location } from '../../types';
import { calculateDistance, formatDistance } from '../../utils/distance';
import './LocationsListView.css';

interface LocationsListViewProps {
  title: string;
  icon?: React.ComponentType<{ size?: number }>;
  locations: Array<Location & { distance?: number }>;
  allLocations?: Record<string, Location>;
  userLocation?: [number, number] | null;
  referenceLocation?: [number, number] | null;
  categoryFilter?: string; // Optional category filter (e.g., 'Beaches')
  onBack: () => void;
  onLocationClick: (location: Location) => void;
}

const ITEMS_PER_PAGE = 20; // Number of items to load per batch

const LocationsListView: React.FC<LocationsListViewProps> = ({
  title,
  icon: Icon,
  locations,
  allLocations = {},
  userLocation,
  referenceLocation,
  categoryFilter,
  onBack,
  onLocationClick,
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [displayedCount, setDisplayedCount] = useState(ITEMS_PER_PAGE);
  const observerTarget = useRef<HTMLDivElement>(null);

  // Use referenceLocation (from Home2) if available, otherwise fall back to userLocation
  const currentLocation = referenceLocation || userLocation || null;
  
  // Determine icon based on title if not provided
  const DisplayIcon = Icon || (title.toLowerCase().includes('beach') ? Waves : ShoppingBag);

  // Get all locations with distance calculations
  const allLocationsWithDistance = useMemo(() => {
    if (!allLocations) return [];

    const locationMap = new Map<string, Location & { distance?: number }>();

    // Add locations from the initial list (already have distances)
    locations.forEach((loc) => {
      if (loc._id) {
        locationMap.set(loc._id, loc);
      }
    });

    // Add all other locations from allLocations and calculate distances
    Object.entries(allLocations).forEach(([key, loc]) => {
      if (!loc || !loc.latitude || !loc.longitude) return;

      // If categoryFilter is specified, only include locations matching that category
      if (categoryFilter && loc.category !== categoryFilter) return;

      const locationWithName = {
        ...loc,
        location: loc.location || key,
        _id: loc._id || key,
      };

      // Skip if already in map
      if (locationMap.has(locationWithName._id)) return;

      // Calculate distance if currentLocation is available
      let distance: number | undefined;
      if (currentLocation) {
        distance = calculateDistance(
          currentLocation[0],
          currentLocation[1],
          locationWithName.latitude,
          locationWithName.longitude
        );
      }

      locationMap.set(locationWithName._id, { ...locationWithName, distance });
    });

    return Array.from(locationMap.values());
  }, [allLocations, locations, currentLocation]);

  // Filter locations based on search query
  const filteredLocations = useMemo(() => {
    if (!searchQuery.trim()) return allLocationsWithDistance;

    const query = searchQuery.toLowerCase();
    return allLocationsWithDistance.filter((loc) => {
      const locationName = (loc.location || loc._id || '').toLowerCase();
      const address = ((loc as any).address || '').toLowerCase();
      const category = (loc.category || '').toLowerCase();
      
      return (
        locationName.includes(query) ||
        address.includes(query) ||
        category.includes(query)
      );
    });
  }, [allLocationsWithDistance, searchQuery]);

  // Sort by distance when currentLocation is available, otherwise by name
  const sortedLocations = useMemo(() => {
    const sorted = [...filteredLocations];
    
    sorted.sort((a, b) => {
      // When using current location, always prioritize distance
      if (currentLocation) {
        // Ensure distance is calculated for all items
        if (a.distance === undefined && a.latitude && a.longitude) {
          a.distance = calculateDistance(
            currentLocation[0],
            currentLocation[1],
            a.latitude,
            a.longitude
          );
        }
        if (b.distance === undefined && b.latitude && b.longitude) {
          b.distance = calculateDistance(
            currentLocation[0],
            currentLocation[1],
            b.latitude,
            b.longitude
          );
        }
        if (a.distance !== undefined && b.distance !== undefined) {
          return a.distance - b.distance;
        }
        if (a.distance !== undefined) return -1;
        if (b.distance !== undefined) return 1;
      }
      // Otherwise sort by name
      return (a.location || a._id || '').localeCompare(b.location || b._id || '');
    });

    return sorted;
  }, [filteredLocations, currentLocation]);

  // Get displayed items (paginated)
  const displayedItems = useMemo(() => {
    return sortedLocations.slice(0, displayedCount);
  }, [sortedLocations, displayedCount]);

  // Infinite scroll using Intersection Observer
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && displayedCount < sortedLocations.length) {
          // Load more items
          setDisplayedCount((prev) => Math.min(prev + ITEMS_PER_PAGE, sortedLocations.length));
        }
      },
      {
        root: null,
        rootMargin: '100px', // Start loading before reaching the bottom
        threshold: 0.1,
      }
    );

    const target = observerTarget.current;
    if (target) {
      observer.observe(target);
    }

    return () => {
      if (target) {
        observer.unobserve(target);
      }
    };
  }, [displayedCount, sortedLocations.length]);

  // Reset displayed count when search query changes
  useEffect(() => {
    setDisplayedCount(ITEMS_PER_PAGE);
  }, [searchQuery]);

  const handleLocationClick = useCallback((location: Location) => {
    onLocationClick(location);
  }, [onLocationClick]);

  return (
    <motion.div
      initial={{ x: '100%' }}
      animate={{ x: 0 }}
      exit={{ x: '100%' }}
      transition={{ type: 'spring', damping: 25, stiffness: 300 }}
      className="locations-list-view"
    >
      {/* Header */}
      <div className="locations-list-header">
        <button className="locations-list-back-button" onClick={onBack}>
          <ArrowLeft size={24} />
        </button>
        <div className="locations-list-title-section">
          <div className="locations-list-icon">
            <DisplayIcon size={24} />
          </div>
          <h1 className="locations-list-title">{title}</h1>
        </div>
        <div style={{ width: 40 }} /> {/* Spacer for centering */}
      </div>

      {/* Search Bar */}
      <div className="locations-list-search-container">
        <div className="locations-list-search-bar">
          <Search size={20} className="locations-list-search-icon" />
          <input
            type="text"
            placeholder="Search locations"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="locations-list-search-input"
          />
        </div>
      </div>

      {/* Locations List */}
      <div className="locations-list-content">
        {displayedItems.length === 0 ? (
          <div className="locations-list-empty">
            <p>No locations found</p>
          </div>
        ) : (
          <>
            {displayedItems.map((location, index) => {
              const locationName = location.location || location._id || 'Unknown Location';
              const distance = location.distance ? formatDistance(location.distance) : null;
              const address = (location as any).address || '';
              const subtitle = address || (location.category || '');

              return (
                <motion.div
                  key={location._id || location.location || index}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: Math.min(index * 0.02, 0.3) }}
                  className="locations-list-item"
                  onClick={() => handleLocationClick(location)}
                >
                  <div className="locations-list-item-icon">
                    <DisplayIcon size={24} />
                  </div>
                  <div className="locations-list-item-content">
                    <h3 className="locations-list-item-name">{locationName}</h3>
                    {subtitle && (
                      <p className="locations-list-item-subtitle">{subtitle}</p>
                    )}
                  </div>
                  <div className="locations-list-item-right">
                    {distance && (
                      <span className="locations-list-item-distance">{distance}</span>
                    )}
                    <ChevronRight size={20} className="locations-list-item-arrow" />
                  </div>
                </motion.div>
              );
            })}
            
            {/* Loading trigger element for infinite scroll */}
            {displayedCount < sortedLocations.length && (
              <div ref={observerTarget} className="locations-list-loading-trigger">
                <div className="locations-list-loading-spinner" />
              </div>
            )}
            
            {/* End of list indicator */}
            {displayedCount >= sortedLocations.length && sortedLocations.length > 0 && (
              <div className="locations-list-end">
                <p>Showing all {sortedLocations.length} locations</p>
              </div>
            )}
          </>
        )}
      </div>
    </motion.div>
  );
};

export default LocationsListView;

